// What an answer checked — the receipt under it (answer-surface spec, §1).
//
// SOURCES, NOT TOOLS. A reader knows "the schedule" and "issues"; they have no
// model of `query_open_work`, and naming one would be the implementation leaking
// into the product. Nor is a source ever the upstream system: which adapter a
// record came from is internal (frontend invariant 1), so the catalog speaks in
// D-ORCA's own domains.
//
// Here, in `shared`, because both sides read it: each backend read tool declares
// which of these it reads, and the frontend names each one through i18n. Server-
// safe — this module is not under `shared/src/client/`.

/** Every source a receipt can name, in no particular order — the receipt keeps the turn's. */
export const CHECK_SOURCES = [
  'schedule',
  'activities',
  'issues',
  'workforce',
  'safety',
  'movement',
  'attendance',
  'productivity',
  'deliveries',
  'stock',
  'documents',
  'weather',
] as const;

export type CheckSource = (typeof CHECK_SOURCES)[number];

/**
 * How a source fared in one turn. `partial` is a source read only in part while
 * the call itself succeeded (a degraded brief section, one programme of three);
 * `failed` is a source that could not be read at all — a call that threw, or a
 * composite's sub-read it caught and reported as a gap.
 */
export const CHECK_OUTCOMES = ['read', 'partial', 'failed'] as const;

export type CheckOutcome = (typeof CHECK_OUTCOMES)[number];

/** One line item of an answer's receipt. */
export interface AiCheck {
  source: CheckSource;
  outcome: CheckOutcome;
}

const SOURCE_SET: ReadonlySet<string> = new Set(CHECK_SOURCES);
const OUTCOME_SET: ReadonlySet<string> = new Set(CHECK_OUTCOMES);

export function isCheckSource(value: unknown): value is CheckSource {
  return typeof value === 'string' && SOURCE_SET.has(value);
}

/**
 * Which source each morning-brief SECTION reads. `query_site_brief` reports per
 * section (its `brief.<key>` status events), so a section that degraded marks
 * its own source `partial` rather than the whole brief.
 *
 * Keyed by the section key as a literal: `shared` cannot import the backend's
 * `BRIEF_SECTIONS`, so a backend test pins this map against it — a section added
 * there without a source here fails CI rather than vanishing from receipts. The
 * literal keys also let the backend index it by a section key and get a source
 * back, never `undefined`: a section missing here is a compile error there.
 */
export const BRIEF_SECTION_SOURCES = {
  safety: 'safety',
  scheduleChanges: 'schedule',
  issues: 'issues',
  activityVsPlan: 'activities',
  todaySchedule: 'schedule',
  weather: 'weather',
} as const satisfies Readonly<Record<string, CheckSource>>;

/**
 * A stored or streamed receipt, read leniently.
 *
 * A thread lives 90 days (the conversations TTL), and a source this build no
 * longer knows must not fail the whole thread — it is dropped, the rest shown.
 * The navigation catalog reads stored links the same way, for the same reason.
 * A source listed twice keeps its first entry: the writer never repeats one, so
 * a repeat is damage, not information.
 */
export function readChecks(raw: unknown): AiCheck[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const checks: AiCheck[] = [];
  for (const item of raw as unknown[]) {
    if (typeof item !== 'object' || item === null) continue;
    const { source, outcome } = item as Record<string, unknown>;
    if (!isCheckSource(source) || typeof outcome !== 'string' || !OUTCOME_SET.has(outcome)) continue;
    if (seen.has(source)) continue;
    seen.add(source);
    checks.push({ source, outcome: outcome as CheckOutcome });
  }
  return checks;
}
